import Component from '@glimmer/component'
import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'
import type Assignment from 'teacher-ui-client/models/assignment'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import { trackedFunction } from 'reactiveweb/function'

interface Args {
  assignment: Assignment
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class ActivityDescriptionLesson extends Component<Signature> {
  @service intl!: IntlService

  @service lessonInfo!: LessonInfoService

  // so we know which assignment precincts align with lesson precincts for a given product
  get precinctMappingForProduct(): Array<{ precinct: Precinct; assignmentPrecinct: string }> {
    switch (this.args.assignment.product) {
      case 'maths':
        return [
          { precinct: Precinct.MS_LESSONS, assignmentPrecinct: 'my_lessons' },
          {
            precinct: Precinct.MS_DRIVING_TESTS,
            assignmentPrecinct: 'driving_tests',
          },
        ]
      case 'writing_legends': {
        return [{ precinct: Precinct.WL_WRITING, assignmentPrecinct: 'lessons' }]
      }
      case 'rex':
        return [
          {
            precinct: Precinct.REX_READING,
            assignmentPrecinct: 'my_lessons',
          },
          {
            precinct: Precinct.REX_SPELLING,
            assignmentPrecinct: 'english_skills_spelling',
          },
          {
            precinct: Precinct.REX_LIBRARY,
            assignmentPrecinct: 'library',
          },
        ]
      case 're':
        return [
          {
            precinct: Precinct.RE_READING,
            assignmentPrecinct: 'lessons',
          },
          {
            precinct: Precinct.RE_SPELLING,
            assignmentPrecinct: 'spelling',
          },
          {
            precinct: Precinct.RE_STORYLANDS,
            assignmentPrecinct: 'clinker_castle',
          },
          {
            precinct: Precinct.RE_DRIVING_TESTS,
            assignmentPrecinct: 'driving_tests',
          },
        ]
      case 'fast_phonics':
        return [
          {
            precinct: Precinct.FP_PEAKS,
            assignmentPrecinct: 'lessons',
          },
        ]
      default:
        return []
    }
  }

  // retrieve the cached lesson precinct type for given assignment precinct
  get lessonPrecinct(): Precinct | undefined {
    const cachedPrecinct = this.precinctMappingForProduct?.find((option) => {
      return option.assignmentPrecinct === this.args.assignment.precinct
    })?.precinct

    return cachedPrecinct
  }

  loadAssignmentDescription = trackedFunction(this, async () => {
    const { assignment_id } = this.args.assignment.assignmentDetails

    if (!this.lessonPrecinct) {
      return
    }

    await this.lessonInfo.loadLessonInfo(this.lessonPrecinct)

    switch (this.lessonPrecinct) {
      case Precinct.RE_READING:
      case Precinct.RE_SPELLING:
      case Precinct.RE_STORYLANDS:
      case Precinct.REX_READING:
      case Precinct.REX_SPELLING:
      case Precinct.MS_LESSONS: {
        const lessons = this.lessonInfo.lessonsFor(this.lessonPrecinct)
        const lesson = lessons.find((lesson) => assignment_id === String(lesson.lesson_number))
        return lesson?.name
      }
      // Driving tests lessons have the same assignment_ids, have to differentiate by category
      case Precinct.RE_DRIVING_TESTS:
      case Precinct.MS_DRIVING_TESTS: {
        const lessons = this.lessonInfo.lessonsFor(this.lessonPrecinct)
        const { category } = this.args.assignment.assignmentDetails

        const lesson = lessons.find(
          (lesson) => category === lesson.category && assignment_id === String(lesson.lesson_number),
        )
        if (!lesson) return

        const productKey = this.args.assignment.product === 're' ? 're' : 'maths'
        const formattedCategory = this.intl.t(`driving_tests.${productKey}.categories.${lesson.category}`)

        return `${formattedCategory} ${lesson.name}`
      }
      // FP Peaks lessons have non-descriptive names (same as lesson title), so use try to use sounds or words instead
      case Precinct.FP_PEAKS: {
        const lessons = this.lessonInfo.lessonsFor(this.lessonPrecinct)
        const lesson = lessons.find((lesson) => assignment_id === String(lesson.lesson_number))

        if (!lesson) return
        if (lesson.sounds.length > 0) {
          return `Focus sounds: ${lesson.sounds.join(', ')}`
        } else if (lesson.words.length > 0) {
          return `Words read: ${lesson.words.join(', ')}`
        } else {
          return lesson.name
        }
      }
      default: {
        return undefined
      }
    }
  })

  get assignmentDescription() {
    // TODO: 29/07/2025 FP assignments now have a description field when created we can access directly. There are still
    // quite a few assignments created before this field was added, so keeping this logic for 3 months to handle most of
    // these long-lived assignments. A scheduled follow up task in JIRA to remove this logic has been created.
    if (!this.args.assignment.assignmentDetails.description) {
      return this.loadAssignmentDescription.value
    }

    return this.args.assignment.assignmentDetails.description
  }

  <template>
    <div ...attributes>
      <strong
        class="block text-left font-bold"
        data-test-lesson-activity-assignment-title
      >{{@assignment.title}}</strong>
      {{#if this.assignmentDescription}}
        <span class="text-sm" data-test-lesson-activity-assignment-desc>
          {{this.assignmentDescription}}
        </span>
      {{/if}}
    </div>
  </template>
}
